/**
 * NBPLCEvidenceVault — Evidence Vault with backend persistence
 * Route: /corporate/nbplc/evidence-vault
 *
 * SSOT: Evidence completeness persisted to nb_evidence_completeness table.
 * VRS score drops when evidence is missing. Art.6 LoA and financing flagged
 * as conditional until evidence reaches 100%.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNBPLC } from "@/context/NBPLCContext";
import { PageHeader, SectionCard } from "@/components/ds";
import { CheckCircle2, AlertTriangle, Clock, Upload, RefreshCw, Shield } from "lucide-react";
import client from "@/api/client";

const BREWERY_EVIDENCE = [
  { field:"electricity_bills",     label:"Monthly electricity bills",         tier:"T1", required:true,  desc:"Grid utility invoices (kWh + cost)" },
  { field:"fuel_invoices",         label:"Fuel purchase invoices",            tier:"T1", required:true,  desc:"Diesel/gas delivery records" },
  { field:"production_records",    label:"Monthly production records (hl)",   tier:"T1", required:true,  desc:"Brewhouse production volumes per plant" },
  { field:"water_meter_readings",  label:"Water meter readings",              tier:"T1", required:true,  desc:"Plant-level water consumption (hl/month)" },
  { field:"packaging_procurement", label:"Packaging procurement data",        tier:"T1", required:true,  desc:"Glass/can/PET purchase records" },
  { field:"refrigerant_log",       label:"Refrigerant top-up records",        tier:"T1", required:true,  desc:"R-134a / R-404A refrigerant log" },
  { field:"solar_generation",      label:"Solar generation report",           tier:"T1", required:false, desc:"Inverter data or generation certificate" },
  { field:"transport_records",     label:"Distribution transport records",    tier:"T2", required:false, desc:"Fuel/distance for outbound logistics" },
  { field:"waste_disposal",        label:"Waste disposal records",            tier:"T2", required:false, desc:"Waste contractor records + recycling %" },
  { field:"agricultural_purchases",label:"Sorghum/barley purchase records",  tier:"T3", required:false, desc:"Procurement data for agricultural inputs" },
];

const PLANTS = [
  { asset_id:"AST-NB-IB", name:"NB Ibadan Brewery" },
  { asset_id:"AST-NB-LA", name:"NB Lagos Brewery"  },
  { asset_id:"AST-NB-AB", name:"NB Aba Brewery"    },
  { asset_id:"AST-NB-KD", name:"NB Kaduna Brewery" },
  { asset_id:"AST-NB-EN", name:"NB Enugu Brewery"  },
];

const REQUIRED_COUNT = BREWERY_EVIDENCE.filter(e => e.required).length;
const TOTAL_COUNT    = BREWERY_EVIDENCE.length;

function EvidenceRow({ item, uploaded, onToggle }) {
  const Icon = uploaded ? CheckCircle2 : item.required ? AlertTriangle : Clock;
  const color = uploaded ? "text-emerald-500" : item.required ? "text-amber-500" : "text-slate-400";
  const tierColors = { T1:"bg-red-100 text-red-700", T2:"bg-amber-100 text-amber-700", T3:"bg-slate-100 text-slate-500" };
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-slate-50 last:border-0">
      <div className="flex items-center gap-2 flex-1">
        <Icon size={13} className={color}/>
        <div>
          <p className="text-xs font-semibold text-slate-800">{item.label}</p>
          <p className="text-[10px] text-slate-400">{item.desc}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 ml-2 flex-shrink-0">
        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${tierColors[item.tier]}`}>{item.tier}</span>
        {item.required && !uploaded && <span className="text-[9px] font-bold text-red-500">Required</span>}
        {uploaded && <span className="text-[9px] font-bold text-emerald-600">Uploaded</span>}
        <button onClick={() => onToggle(item.field)}
          className={`px-2 py-1 text-[10px] font-semibold rounded-lg border transition-colors ${
            uploaded ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                     : "bg-white text-slate-500 border-slate-200 hover:border-teal-300"
          }`}>
          {uploaded ? "✓ Marked" : <span className="flex items-center gap-1"><Upload size={9}/> Mark uploaded</span>}
        </button>
      </div>
    </div>
  );
}

export default function NBPLCEvidenceVault() {
  const { period, selectedPeriod, periods, setSelectedPeriod } = useNBPLC();
  const [selectedAsset, setSelectedAsset] = useState(PLANTS[0].asset_id);
  const [uploadedFields, setUploadedFields] = useState({});  // { field: bool }
  const [saving, setSaving] = useState(false);
  const [evidenceStatus, setEvidenceStatus] = useState(null); // backend status per asset

  const uploadedCount = Object.values(uploadedFields).filter(Boolean).length;
  const requiredUploaded = BREWERY_EVIDENCE.filter(e => e.required && uploadedFields[e.field]).length;
  const completeness  = Math.round(uploadedCount / TOTAL_COUNT * 100);
  const reqCompleteness = Math.round(requiredUploaded / REQUIRED_COUNT * 100);

  // Load evidence status from backend (SSOT)
  const loadStatus = useCallback(() => {
    if (!selectedPeriod) return;
    client.get(`/nbplc/evidence-status/${selectedPeriod}`)
      .then(r => {
        setEvidenceStatus(r.data);
        const assetRow = r.data.by_asset?.find(a => a.asset_id === selectedAsset);
        if (assetRow && assetRow.items_uploaded > 0) {
          // Reconstruct uploaded state: mark the first N items as uploaded (in order)
          // Required items first (electricity, fuel, production, water, packaging, refrigerant)
          const newUploaded = {};
          BREWERY_EVIDENCE.slice(0, assetRow.items_uploaded).forEach(e => { newUploaded[e.field] = true; });
          setUploadedFields(newUploaded);
        } else {
          setUploadedFields({});
        }
      })
      .catch(() => {});
  }, [selectedPeriod, selectedAsset]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Persist evidence status to backend
  const persistEvidence = useCallback(async (newUploaded) => {
    if (!selectedPeriod || !selectedAsset) return;
    setSaving(true);
    const count = Object.values(newUploaded).filter(Boolean).length;
    try {
      await client.post("/nbplc/evidence-status", {
        period_id: selectedPeriod,
        asset_id: selectedAsset,
        items_uploaded: count,
        items_required: REQUIRED_COUNT,
        items_total: TOTAL_COUNT,
      });
      await loadStatus();
    } finally { setSaving(false); }
  }, [selectedPeriod, selectedAsset, loadStatus]);

  const handleToggle = (field) => {
    const next = { ...uploadedFields, [field]: !uploadedFields[field] };
    setUploadedFields(next);
    persistEvidence(next);
  };

  // System impact: VRS drops if evidence < 100%
  const vrsImpact = completeness >= 100
    ? "Evidence complete — VRS at full potential (100/100)"
    : `VRS currently ~${Math.round(100 * 0.60 + completeness * 0.40)}/100 — upload all 10 items to reach VRS 100`;
  const art6Blocked = reqCompleteness < 80;
  const financingConditional = reqCompleteness < 100;

  const overallPct = evidenceStatus?.overall_pct || 0;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">Nigeria Breweries Plc</p>
          <PageHeader title="Evidence Vault" />
          <p className="text-xs text-slate-400 mt-0.5">{period?.label} · Evidence drives VRS score + Art.6 readiness</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={selectedPeriod||""} onChange={e=>setSelectedPeriod(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white">
            {periods.map(p=><option key={p.period_id} value={p.period_id}>{p.label}</option>)}
          </select>
          <select value={selectedAsset} onChange={e => { setSelectedAsset(e.target.value); setUploadedFields({}); }}
            className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white">
            {PLANTS.map(p => <option key={p.asset_id} value={p.asset_id}>{p.name}</option>)}
          </select>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${
            completeness >= 100 ? "bg-emerald-100 text-emerald-700 border-emerald-200"
            : "bg-amber-100 text-amber-700 border-amber-200"
          }`} data-testid="nb-evidence-completeness">
            {completeness}% uploaded
          </span>
          {saving && <RefreshCw size={12} className="animate-spin text-slate-400"/>}
        </div>
      </div>

      {/* SSOT System Impact Panel */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className={`rounded-xl p-3 border ${completeness >= 100 ? "bg-teal-50 border-teal-200" : "bg-amber-50 border-amber-200"}`}>
          <p className="text-[9px] font-bold uppercase tracking-wide text-slate-500 mb-1">VRS Score Impact</p>
          <p className={`text-xs font-semibold ${completeness >= 100 ? "text-teal-700" : "text-amber-700"}`}>{vrsImpact}</p>
        </div>
        <div className={`rounded-xl p-3 border ${!art6Blocked ? "bg-teal-50 border-teal-200" : "bg-red-50 border-red-200"}`}>
          <p className="text-[9px] font-bold uppercase tracking-wide text-slate-500 mb-1">Article 6 LoA Readiness</p>
          <p className={`text-xs font-semibold ${!art6Blocked ? "text-teal-700" : "text-red-700"}`}>
            {art6Blocked
              ? `Blocked — ${REQUIRED_COUNT - requiredUploaded} required docs missing`
              : "Cleared — required docs uploaded"}
          </p>
        </div>
        <div className={`rounded-xl p-3 border ${!financingConditional ? "bg-teal-50 border-teal-200" : "bg-amber-50 border-amber-200"}`}>
          <p className="text-[9px] font-bold uppercase tracking-wide text-slate-500 mb-1">Financing Bankability</p>
          <p className={`text-xs font-semibold ${!financingConditional ? "text-teal-700" : "text-amber-700"}`}>
            {financingConditional
              ? "Conditional — evidence incomplete reduces bankability"
              : "Full bankability — all required docs uploaded"}
          </p>
        </div>
      </div>

      {/* Portfolio completeness strip */}
      {evidenceStatus && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between text-xs mb-2">
            <p className="font-semibold text-slate-700">Portfolio Evidence Completeness</p>
            <span className="font-bold text-slate-500">{overallPct}% across all plants</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${overallPct >= 100 ? "bg-emerald-500" : "bg-amber-400"}`}
              style={{width:`${overallPct}%`}} />
          </div>
          <div className="flex gap-2 mt-2 flex-wrap">
            {(evidenceStatus.by_asset || []).map(a => {
              const plant = PLANTS.find(p => p.asset_id === a.asset_id);
              return (
                <span key={a.asset_id} className="text-[9px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5">
                  {(plant?.name||a.asset_id).replace("NB ","").split(" ")[0]}: {a.pct_uploaded||0}%
                </span>
              );
            })}
          </div>
        </div>
      )}

      {completeness < 100 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-2">
          <AlertTriangle size={12} className="text-amber-500 flex-shrink-0"/>
          <p className="text-xs text-amber-700 font-semibold">
            Evidence completeness: {completeness}% — Insufficient for external verification
          </p>
        </div>
      )}

      <SectionCard>
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs text-slate-700 font-bold">
              {PLANTS.find(p => p.asset_id === selectedAsset)?.name} — Required Evidence
            </p>
            <p className="text-[9px] text-slate-400 mt-0.5">
              {REQUIRED_COUNT} required · {TOTAL_COUNT - REQUIRED_COUNT} optional · Mark items as uploaded to persist
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm font-bold text-slate-900">{uploadedCount}/{TOTAL_COUNT}</p>
            <p className="text-[9px] text-slate-400">items uploaded</p>
          </div>
        </div>

        {BREWERY_EVIDENCE.map(item => (
          <EvidenceRow key={item.field} item={item}
            uploaded={!!uploadedFields[item.field]}
            onToggle={handleToggle} />
        ))}

        <div className="mt-4 p-3 bg-slate-50 rounded-lg">
          <p className="text-[9px] text-slate-500 mb-1 font-semibold uppercase tracking-wide">Evidence Tiers</p>
          <p className="text-xs text-slate-500">
            <strong>T1 (Primary):</strong> Measured data — highest confidence, required for GHG Protocol compliance.{" "}
            <strong>T2 (Secondary):</strong> Calculated from activity data.{" "}
            <strong>T3 (Estimated):</strong> Industry averages — acceptable for initial reporting.
          </p>
          <p className="text-[9px] text-slate-400 mt-1 flex items-center gap-1">
            <Shield size={9}/> Evidence uploads are persisted and affect VRS score, Art.6 readiness, and financing bankability.
          </p>
        </div>
      </SectionCard>
    </div>
  );
}
